import { type FileSaverOptions } from 'file-saver';

/**
 * Downloads data as a file using file-saver.
 * Handles browser quirks and provides a consistent download experience.
 *
 * @param data - The content to download (string or Blob)
 * @param filename - Optional filename for the download
 * @param options - Optional file-saver options
 */
export async function download(data: string | Blob, filename?: string, options?: FileSaverOptions): Promise<void> {
  const fileSaver = await import('file-saver');

  // file-saver treats strings as URLs, so we need to convert to Blob
  const blob = typeof data === 'string' ? new Blob([data], { type: 'text/plain;charset=utf-8' }) : data;

  return fileSaver.default.saveAs(blob, filename, options);
}

/** One text file inside a zip. */
export type ZipTextFile = { name: string; content: string };

/**
 * Zips text files and downloads the archive, matching the house pattern (jszip
 * + file-saver). `jszip` is imported lazily so it stays out of the initial
 * bundle — only the click that exports pays for it.
 */
export async function downloadZip(filename: string, files: ZipTextFile[]): Promise<void> {
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  for (const file of files) {
    zip.file(file.name, file.content);
  }
  return download(await zip.generateAsync({ type: 'blob' }), filename);
}
